import React, { useEffect, useMemo, useState } from 'react';
import { CopyTextButton } from './CopyTextButton';
import { EntityImage } from './EntityImage';
import { resolveImageEntries } from '../../utils/entityImages';
import { AUDIT_CHECK_IDS, PACKS, buildMonsterPhotoAudit, formatMonsterPhotoAudit, sheetKey, type SheetLoad } from '../../utils/monsterPhotoAudit';
import type { WorldData } from '../../types/world';
const totalSheets = PACKS.reduce((n, p) => n + Object.keys(p.sheets).length, 0);

/**
 * Panel del staff: diagnóstico combinado de los paquetes LIMPIOS (10 láminas) y EXTRA (3 láminas).
 * Recibido → cargado → asignado → visible, y tres controles separados: carga, identidad y encuadre.
 */
export function MonsterPhotoAudit({
  w
}: {
  w: WorldData;
}) {
  const {
    byMonster
  } = useMemo(() => resolveImageEntries(w), [w]);
  const [run, setRun] = useState(false);
  const [sheets, setSheets] = useState<Record<string, SheetLoad>>({});
  useEffect(() => {
    if (!run) return;
    PACKS.forEach(p => Object.entries(p.sheets).forEach(([n, url]) => {
      const k = sheetKey(p.key, Number(n));
      setSheets(s => ({
        ...s,
        [k]: {
          status: 'cargando'
        }
      }));
      const img = new Image();
      img.onload = () => setSheets(s => ({
        ...s,
        [k]: {
          status: 'ok',
          w: img.naturalWidth,
          h: img.naturalHeight
        }
      }));
      img.onerror = () => setSheets(s => ({
        ...s,
        [k]: {
          status: 'error'
        }
      }));
      img.src = url;
    }));
  }, [run]);
  const audit = useMemo(() => buildMonsterPhotoAudit(w, sheets, run), [w, sheets, run]);
  const report = useMemo(() => formatMonsterPhotoAudit(audit, run), [audit, run]);
  const loading = run && Object.values(sheets).some(s => s.status === 'cargando');
  return <section aria-labelledby="mob-audit" className="mt-2" data-id="element-1595" data-mp="8bv4gmgf65pu">
      <h3 id="mob-audit" className="text-lg font-semibold text-white" data-id="element-1596" data-mp="8dfwo5f9hckj">Fotos de monstruos: paquetes LIMPIOS + EXTRA</h3>
      <p className="mt-1 max-w-3xl text-sm text-gray-400" data-id="element-1597" data-mp="i9j9q6bnivm9">
        Un solo diagnóstico para las {totalSheets} láminas. El índice compacto no llegó al proyecto: los IDs salen de los rótulos impresos. Carga, identidad y encuadre se comprueban por separado.
      </p>
      <div className="mt-3 flex flex-wrap gap-2" data-id="element-1598" data-mp="29brmcdcvg5c3">
        <button type="button" onClick={() => setRun(true)} disabled={loading} className="h-9 rounded-xl border border-white/10 px-3 text-sm font-semibold text-white transition-colors duration-150 hover:border-[#D4AF37]/40 disabled:opacity-60" data-id="element-1599" data-mp="2o30il5ba4oy7">
          {loading ? 'Comprobando láminas…' : `Comprobar las ${totalSheets} láminas`}
        </button>
        <CopyTextButton text={report} label="Copiar diagnóstico de fotos" data-id="element-1600" data-mp="371uyihmhbvfy" />
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-white/10" data-id="element-1601" data-mp="322run0i8jv9k">
        <table className="w-full min-w-[720px] text-left text-sm" data-id="element-1602" data-mp="1cnk8w6y28x94">
          <caption className="sr-only" data-id="element-1603" data-mp="1cnu0u02o1ehg">Etapas por paquete</caption>
          <thead className="bg-white/[0.03] text-xs text-gray-400" data-id="element-1604" data-mp="17paa7md4neir">
            <tr data-id="element-1605" data-mp="3jbufzvusqb3e">
              <th className="px-4 py-2.5 font-semibold" data-id="element-1606" data-mp="3jbawiaphzen9">Paquete</th>
              <th className="px-4 py-2.5 font-semibold" data-id="element-1607" data-mp="3je6ufz1ci3bp">Recibido</th>
              <th className="px-4 py-2.5 font-semibold" data-id="element-1608" data-mp="3oae2pyh7rqwt">Cargado</th>
              <th className="px-4 py-2.5 font-semibold" data-id="element-1609" data-mp="39mswhvp1lbko">Asignado</th>
              <th className="px-4 py-2.5 font-semibold" data-id="element-1610" data-mp="3el46c6fc7m2w">Visible</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5 text-gray-200" data-id="element-1611" data-mp="30lco1thsfjj3">
            {audit.packs.map(p => <tr key={p.key} className="align-top" data-id="element-1612" data-mp="35k64nusdfqnh">
                <td className="px-4 py-3 font-semibold text-white" data-id="element-1613" data-mp="35lqw3bje2th4">{p.name}</td>
                <td className="px-4 py-3" data-id="element-1614" data-mp="2qogmy8u7xzok">
                  {p.received.ids} IDs · {p.received.photos} fotos · {p.received.sheets} láminas
                  <span className={`block text-xs ${p.received.ids === p.reference.ids ? 'text-gray-500' : 'text-amber-200'}`} data-id="element-1615" data-mp="2vo306ia1rfxd">
                    Referencia: {p.reference.ids} IDs{p.reference.photos ? ` · ${p.reference.photos} fotos` : ''}
                  </span>
                </td>
                <td className="px-4 py-3" data-id="element-1616" data-mp="2pvxcxdui4944">
                  {run ? `${p.loaded.ok}/${p.received.sheets}` : 'Sin comprobar'}
                  {p.loaded.failed.length > 0 && <span className="block text-xs text-red-200" data-id="element-1617" data-mp="2gwaaqpty6za0">No cargan: {p.loaded.failed.join(', ')}</span>}
                  {p.loaded.resized.length > 0 && <span className="block text-xs text-amber-200" data-id="element-1618" data-mp="2c0csuf31dkv4">Otro tamaño: {p.loaded.resized.join('; ')}</span>}
                </td>
                <td className="px-4 py-3" data-id="element-1619" data-mp="227xcfm7e14cc">
                  {p.assigned.thisPack} usan esta foto
                  <span className="block text-xs text-gray-500" data-id="element-1620" data-mp="1y6c48e5ste8h">
                    {p.assigned.keptStaff} del staff conservadas · {p.assigned.keptOther} otra lámina · {p.assigned.notInCatalog} fuera de 06_monsters
                  </span>
                </td>
                <td className="px-4 py-3" data-id="element-1621" data-mp="1oas9hv2z0llb">{p.visible ?? 'Sin comprobar'}</td>
              </tr>)}
          </tbody>
        </table>
      </div>

      <dl className="mt-4 grid gap-3 text-sm md:grid-cols-3" data-id="element-1622" data-mp="196fq13kphf2h">
        {[{
        label: 'Carga',
        value: run ? audit.packs.reduce((n, p) => n + p.loadIssues.length, 0) : undefined,
        note: 'IDs cuya lámina no cargó'
      }, {
        label: 'Identidad',
        value: audit.packs.reduce((n, p) => n + p.identityIssues.length, 0),
        note: 'nombre distinto o ID fuera de 06_monsters'
      }, {
        label: 'Encuadre',
        value: audit.packs.reduce((n, p) => n + p.framingIssues.length, 0),
        note: 'fuera de su celda o reemplazo recomendado'
      }].map(c => <div key={c.label} className="rounded-xl bg-white/[0.03] px-4 py-3" data-id="element-1623" data-mp="vc55kyfqs728">
            <dt className="text-xs text-gray-400" data-id="element-1624" data-mp="10c1dv9vh2knl">Control de {c.label.toLowerCase()}</dt>
            <dd className="mt-0.5 font-mono text-xl font-semibold text-white" data-id="element-1625" data-mp="155m2wcf384an">{c.value ?? '—'}</dd>
            <dd className="text-xs text-gray-500" data-id="element-1626" data-mp="7o5jwcxaf6ui">{c.value === undefined ? 'Sin comprobar' : c.note}</dd>
          </div>)}
      </dl>

      <p className="mt-4 text-sm text-gray-300" data-id="element-1627" data-mp="4n2fajg9s7al">
        Catálogo 06_monsters: {audit.catalogTotal} · con foto {audit.withImage} · sin foto {audit.pending.length}
        <span className="block text-xs text-gray-500" data-id="element-1628" data-mp="3vsu0d4f6wtqj">
          Monstruos {audit.byClass('monstruo').pending.length} · NPC {audit.byClass('npc').pending.length} · Objetos {audit.byClass('objeto').pending.length} (solo con clasificación documentada)
        </span>
      </p>

      <ul className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-5 lg:grid-cols-7" data-id="element-1629" data-mp="3hunc8g01m842">
        {AUDIT_CHECK_IDS.filter(id => w.monsters.has(id)).map(id => {
        const name = w.monsters.get(id)!.name;
        return <li key={id} className="text-center" data-id="element-1630" data-mp="3rt34imat3nf5">
              <EntityImage entry={byMonster.get(id)} alt={name} name={name} size="card" kind="monstruo" data-id="element-1631" data-mp="3ms5oogd9r5gt" />
              <span className="mt-1 block truncate text-xs text-gray-400" data-id="element-1632" data-mp="170rjoe2v1u6c">{id} · {name}</span>
            </li>;
      })}
      </ul>

      <pre className="mt-4 max-h-72 overflow-auto whitespace-pre-wrap rounded-xl bg-white/[0.03] p-3 text-xs text-gray-300" data-id="element-1633" data-mp="1226j2djp58hd">{report}</pre>
    </section>;
}